import { FormattedMessage } from 'react-intl';
import { getMemberKey } from '../AffordaHousehold/utils';
import {
  FIELD_AMOUNT,
  FIELD_MEMBERS,
  FIELD_TEXT_REQUIRED,
} from './constants.fields';
import { PAY_FREQUENCY } from 'pages/AffordaWagesAndIncome/constants';
import { validateNoSpecialCharsForSSI } from 'utils/validateNoSpecialChars';

export const MESSAGE_IDS = {
  title: 'afforda.wagesAndIncome.details.socialSec.title',
  description: 'afforda.wagesAndIncome.details.socialSec.subtitle',
  addButton: 'afforda.wagesAndIncome.details.socialSec.add',
  addCaption: 'afforda.wagesAndIncome.details.socialSec.addDescription',
};

export const SOCIAL_SECURITY_FIELD_NAMES = {
  memberId: 'memberId',
  source: 'source',
  payFrequency: 'payFrequency',
  endDate: 'endDate',
  weeklyAmount: 'weeklyAmount',
  monthlyAmount: 'monthlyAmount',
  annualAmount: 'annualAmount',
  claimNumber: 'claimNumber',
};

export const NEW_SOCIAL_SEC = {
  [SOCIAL_SECURITY_FIELD_NAMES.memberId]: '',
  [SOCIAL_SECURITY_FIELD_NAMES.source]: '',
  [SOCIAL_SECURITY_FIELD_NAMES.payFrequency]: PAY_FREQUENCY.monthly,
  [SOCIAL_SECURITY_FIELD_NAMES.monthlyAmount]: '',
  [SOCIAL_SECURITY_FIELD_NAMES.claimNumber]: '',
};

export const getDefaultValues = (item) => {
  return {
    ...item,
    id: item.id,
    source: item.source ?? '',
    payFrequency: NEW_SOCIAL_SEC[SOCIAL_SECURITY_FIELD_NAMES.payFrequency],
    monthlyAmount: item.monthlyAmount ?? '',
    claimNumber: item.claimNumber ?? '',
  };
};

export const getAvailableMembers = (members, items) => {
  return members?.filter((member) => {
    const memberKey = getMemberKey(member);
    const hasRecordSsi = items.find(
      (record) =>
        record.memberId === memberKey && record.source === SOCIAL_SEC_SSI,
    );

    const hasRecordSsdi = items.find(
      (record) =>
        record.memberId === memberKey && record.source === SOCIAL_SEC_SSDI,
    );
    return !(hasRecordSsi && hasRecordSsdi);
  });
};

export const getIsAddDisabled = ({ members, items }) => {
  const membersAvailable = getAvailableMembers(members, items);
  return membersAvailable.length > 0;
};

export const SOCIAL_SEC_SSI = 'ssi';
export const SOCIAL_SEC_SSDI = 'ssdi';

export const SOURCE_OPTIONS = {
  WEEKLY: {
    value: SOCIAL_SEC_SSI,
    text: 'afforda.wagesAndIncome.details.socialSec.ssi',
  },
  MONTHLY: {
    value: SOCIAL_SEC_SSDI,
    text: 'afforda.wagesAndIncome.details.socialSec.ssdi',
  },
};

export const FIELD_MEMBER_SELECTED = {
  watchFields: [SOCIAL_SECURITY_FIELD_NAMES.memberId],
  hidden: ({ getValues }) =>
    !getValues(SOCIAL_SECURITY_FIELD_NAMES.memberId)?.length,
};

const getSourceOptions = ({ index, items, memberId }) => {
  // Disable members that already have items with both sources.
  const newRecords = items.slice(0, items.length - 1);

  const availableSources =
    Object.values(SOURCE_OPTIONS).filter((option) => {
      const hasRecord = newRecords.find(
        (record) =>
          record?.memberId === memberId && record.source === option.value,
      );
      return !hasRecord;
    }) ?? [];

  return Object.values(SOURCE_OPTIONS)
    ?.map((option) => {
      const isDisabled = !availableSources
        ?.map(({ value }) => value)
        ?.includes(option.value);
      return {
        value: option.value,
        label: option.text,
        disabled: isDisabled,
      };
    })
    .filter(
      (item) =>
        index === items.length - 1 ||
        (items[index]?.source?.length > 0 &&
          items[index].source === item.value),
    )
    .map((item) => ({
      ...item,
      label: <FormattedMessage id={item.label} />,
    }));
};

export const FIELDS = ({
  intl,
  disabledMembers,
  members,
  items,
  index,
  memberId,
}) => {
  const sourceOptions = getSourceOptions({ index, items, memberId });

  return [
    ...FIELD_MEMBERS({ members, disabledMembers }),
    // Source
    {
      ...FIELD_MEMBER_SELECTED,
      name: SOCIAL_SECURITY_FIELD_NAMES.source,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.socialSec.source" />
      ),
      variant: 'buttonGroup',
      rules: {
        required: true,
      },
      direction: 'column',
      GridProps: {
        xs: 12,
      },
      hidden: ({ getValues }) =>
        !getValues(SOCIAL_SECURITY_FIELD_NAMES.memberId)?.length,
      watchFields: [
        SOCIAL_SECURITY_FIELD_NAMES.memberId,
        SOCIAL_SECURITY_FIELD_NAMES.source,
      ],
      options: sourceOptions,
    },
    // Gross Monthly
    {
      ...FIELD_AMOUNT,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.socialSec.grossMonthlyReceived" />
      ),
      name: SOCIAL_SECURITY_FIELD_NAMES.monthlyAmount,
      GridProps: {
        xs: 12,
        sm: 6,
      },
    },
    // Received
    {
      ...FIELD_TEXT_REQUIRED,
      rules: {
        required: false,
        validate: validateNoSpecialCharsForSSI,
      },
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.socialSec.claimNumber" />
      ),
      placeholder: intl.formatMessage({
        id: 'afforda.wagesAndIncome.details.socialSec.claimNumber',
      }),
      name: SOCIAL_SECURITY_FIELD_NAMES.claimNumber,
      helperText: intl.formatMessage({
        id: 'afforda.wagesAndIncome.details.socialSec.diffSSN',
      }),
      GridProps: {
        xs: 12,
        sm: 6,
      },
    },
  ];
};
